import React, { useState, useEffect } from 'react';
import { Menu, X, Search, ChevronRight, ArrowRight, CheckCircle, Brain, Code, Database, Shield, Stethoscope, FileText, Activity, Users, TrendingUp, Award, Zap, ChevronLeft } from 'lucide-react';

const TechMedicalAgency = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrolly > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-slide functionality
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000); // Change slide every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const heroSlides = [
    {
      title: "Leverage the transform potential of cutting-edge artificial intelligence",
      subtitle: "Highlight the potential benefits of Neural Networks, such as improved decision-making, predictive analytics, and automation.",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1600&h=900&fit=crop",
      pattern: "diagonal-lines"
    },
    {
      title: "Revolutionize Healthcare with Advanced Medical Coding Solutions",
      subtitle: "Streamline your revenue cycle with AI-powered medical coding, ensuring accuracy, compliance, and faster reimbursements.",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1600&h=900&fit=crop",
      pattern: "circuit"
    },
    {
      title: "Transform Your Business with Intelligent Automation",
      subtitle: "Empower your enterprise with machine learning models that drive efficiency, reduce costs, and unlock new opportunities.",
      image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1600&h=900&fit=crop",
      pattern: "neural"
    }
  ];

  const services = [
    {
      icon: <Brain className="w-10 h-10" />,
      title: "AI & Machine Learning",
      description: "Custom AI solutions that learn, adapt, and optimize your business processes in real-time.",
      color: "from-cyan-400 to-blue-500"
    },
    {
      icon: <Stethoscope className="w-10 h-10" />,
      title: "Medical Coding Services",
      description: "Accurate ICD-10, CPT, and HCPCS coding with AI-assisted validation and compliance checks.",
      color: "from-blue-400 to-purple-500"
    },
    {
      icon: <Code className="w-10 h-10" />,
      title: "Custom Software Development",
      description: "Tailored applications built with cutting-edge technologies to meet your unique requirements.",
      color: "from-purple-400 to-pink-500"
    },
    {
      icon: <Database className="w-10 h-10" />,
      title: "Data Analytics & BI",
      description: "Transform raw data into actionable insights with advanced analytics and visualization tools.",
      color: "from-pink-400 to-red-500"
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: "Healthcare Compliance",
      description: "HIPAA-compliant solutions ensuring data security and regulatory adherence in healthcare.",
      color: "from-cyan-400 to-teal-500"
    },
    {
      icon: <Activity className="w-10 h-10" />,
      title: "Revenue Cycle Management",
      description: "Optimize billing workflows and maximize reimbursements with intelligent automation.",
      color: "from-teal-400 to-green-500"
    }
  ];

  const benefits = [
    {
      title: "Experienced team",
      description: "Emphasize the expertise and knowledge of your team in developing and implementing neural networks",
      icon: <Users className="w-8 h-8" />
    },
    {
      title: "Tailored solutions",
      description: "Mention your ability to customize solutions based on specific business requirements",
      icon: <Zap className="w-8 h-8" />
    },
    {
      title: "Cutting-edge technology",
      description: "Highlight your use of the latest tools and techniques in neural network development",
      icon: <TrendingUp className="w-8 h-8" />
    },
    {
      title: "Proven track record",
      description: "Showcase successful case studies or client testimonials that demonstrate the effectiveness of your services",
      icon: <Award className="w-8 h-8" />
    }
  ];

  const howItWorks = [
    {
      number: "01",
      title: "Neurons and Layers",
      description: "A neural network consists of interconnected nodes called neurons. Neurons are organized into layers."
    },
    {
      number: "02",
      title: "Activation Functions",
      description: "Each neuron applies an activation function to the weighted sum of its inputs and produces an output"
    },
    {
      number: "03",
      title: "Feedforward Process",
      description: "The inputs are multiplied by their respective weights, summed up, and passed through the activation function."
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}


      {/* Hero Section with Auto-Slide */}
      <section className="relative h-screen overflow-hidden mt-16">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {/* Background Image with Overlay Pattern */}
            <div className="absolute inset-0">
              <img 
                src={slide.image} 
                alt={`Slide ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-cyan-900/70"></div>
              
              {/* Diagonal Lines Pattern Overlay */}
              <div 
                className="absolute inset-0 opacity-30"
                style={{
                  backgroundImage: `repeating-linear-gradient(
                    45deg,
                    transparent,
                    transparent 10px,
                    rgba(255,255,255,0.1) 10px,
                    rgba(255,255,255,0.1) 12px
                  )`
                }}
              ></div>
            </div>

            {/* Content */}
            <div className="relative container mx-auto px-6 h-full flex items-center">
              <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
                <div className="text-white space-y-6 animate-fade-in">
                  <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                    {slide.title}
                  </h1>
                  <p className="text-xl md:text-2xl text-gray-200 leading-relaxed max-w-2xl">
                    {slide.subtitle}
                  </p>
                  <button className="bg-gradient-to-r from-cyan-400 to-blue-500 text-white px-10 py-4 rounded-full font-medium hover:shadow-2xl transition transform hover:scale-105 inline-flex items-center gap-3 group">
                    DISCOVER
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Slide Navigation */}
        <div className="absolute bottom-12 right-12 flex items-center gap-4 z-10">
          <button 
            onClick={prevSlide}
            className="w-14 h-14 border-2 border-white/50 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition backdrop-blur-sm"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button 
            onClick={nextSlide}
            className="w-14 h-14 border-2 border-white/50 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition backdrop-blur-sm"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 flex gap-3 z-10">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide ? 'w-12 bg-white' : 'w-2 bg-white/50'
              }`}
            />
          ))}
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="text-sm text-gray-500 tracking-widest">EST. 2020</div>
              <h2 className="text-5xl md:text-6xl font-bold leading-tight">
                Leading agency for innovative & contemporary AI solutions
              </h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  Artificial Intelligence refers to the development of computer systems that can perform tasks that would typically require human intelligence. It involves the creation of algorithms and models that enable machines to learn, reason, perceive, and make decisions.
                </p>
                <p>
                  There are generally two types of AI: Narrow or Weak AI, which is designed to perform specific tasks, and General or Strong AI, which possesses human-level intelligence and can handle a wide range of tasks.
                </p>
              </div>
              <button className="border-2 border-gray-300 text-gray-900 px-10 py-4 rounded-full font-medium hover:border-cyan-500 hover:text-cyan-500 transition inline-flex items-center gap-3 group">
                EXPLORE_MORE
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition" />
              </button>
            </div>

            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="h-48 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-3xl"></div>
                  <div className="h-64 bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl"></div>
                </div>
                <div className="space-y-4 pt-12">
                  <div className="h-64 bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl"></div>
                  <div className="h-48 bg-gradient-to-br from-pink-100 to-red-100 rounded-3xl"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 px-6 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="text-sm text-gray-500 tracking-widest mb-4">(_SERVICES)</div>
            <h2 className="text-5xl md:text-6xl font-bold leading-tight mb-6">
              Distinct benefits and competitive advantages of our features
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="group relative bg-white p-8 rounded-2xl hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-transparent"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`}></div>
                
                <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6 text-white transform group-hover:scale-110 transition-transform duration-500`}>
                  {service.icon}
                </div>
                
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                
                <div className="flex items-center text-cyan-500 font-medium group-hover:gap-3 gap-2 transition-all">
                  Learn more
                  <ArrowRight className="w-5 h-5 transform group-hover:translate-x-1 transition" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="text-sm text-gray-500 tracking-widest mb-4">(_SERVICES)</div>
            <h2 className="text-5xl md:text-6xl font-bold leading-tight">
              Distinct benefits and competitive advantages of our features
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                <div className={`w-12 h-12 bg-gradient-to-br ${
                  index === 0 ? 'from-cyan-400 to-cyan-500' :
                  index === 1 ? 'from-blue-400 to-blue-500' :
                  index === 2 ? 'from-purple-400 to-purple-500' :
                  'from-pink-400 to-pink-500'
                } rounded-xl flex items-center justify-center text-white`}>
                  {benefit.icon}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto">
          <div className="mb-16">
            <div className="text-sm text-gray-500 tracking-widest mb-4">(_HOW_IT_WORKS)</div>
            <h2 className="text-5xl md:text-6xl font-bold leading-tight max-w-4xl">
              AI serve as a core element of artificial intelligence (AI) systems
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            {howItWorks.map((step, index) => (
              <div key={index} className="space-y-6">
                <div className="text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500" style={{ WebkitTextStroke: '2px rgba(34, 211, 238, 0.3)' }}>
                  {step.number}
                </div>
                <h3 className="text-2xl font-bold text-gray-900">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 flex justify-center">
            <div className="flex items-center gap-4">
              <button className="w-14 h-14 border-2 border-gray-200 rounded-full flex items-center justify-center hover:border-cyan-500 transition">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button className="w-14 h-14 border-2 border-gray-200 rounded-full flex items-center justify-center hover:border-cyan-500 transition">
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1600&h=900&fit=crop" 
            alt="Testimonials"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-black/90"></div>
        </div>

        <div className="relative container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="text-white space-y-6">
              <h2 className="text-5xl md:text-6xl font-bold leading-tight">
                Discover what our clients have to say about our AI solutions
              </h2>
              <div className="text-8xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                250+
              </div>
              <p className="text-2xl text-gray-300">Happy clients</p>
            </div>

            <div className="bg-white/10 backdrop-blur-lg p-10 rounded-3xl border border-white/20">
              <div className="text-6xl mb-8 text-cyan-400">"</div>
              <p className="text-xl text-white leading-relaxed mb-8">
                Working with this agency has been a game-changer for our business. Their AI solutions have revolutionized our operations, enabling us to automate repetitive tasks and make data-driven decisions with ease. We couldn't be happier with the results.
              </p>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full"></div>
                <div>
                  <div className="text-white font-bold text-lg">John Anderson</div>
                  <div className="text-gray-400">CEO of XYZ Company</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=1600&h=600&fit=crop" 
            alt="CTA Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-100/95 to-white/90"></div>
        </div>

        <div className="relative container mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-bold leading-tight mb-8">
            Boost performance and efficiency powered by Agency Neural Networks
          </h2>
          <button className="border-2 border-gray-900 text-gray-900 px-12 py-5 rounded-full font-medium hover:bg-gray-900 hover:text-white transition inline-flex items-center gap-3 group">
            EXPLORE_MORE
            <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition" />
          </button>
        </div>
      </section>

      {/* Scroll to Top Button */}
      <button 
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 w-14 h-14 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white shadow-xl hover:shadow-2xl transition-all transform hover:scale-110 z-40"
      >
        <ChevronRight className="w-6 h-6 rotate-[-90deg]" />
      </button>

      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TechMedicalAgency;